import { adminSupabase } from './supabaseService'; // Use optimized client
import { Keypair } from '@solana/web3.js';
import {
    encryptPrivateKey,
    generateNewKeypair,
    createKeypairBackup,
    generatePassphrase,
    decryptPrivateKey,
    encryptPrivateKeyWithPassword,
    uint8ArrayToString
} from '../utils/walletCryptoUtils';
import { getBalance } from './shyftService';

// Trading wallet cache for performance
const tradingWalletCache = new Map<string, { wallets: TradingWallet[], timestamp: number }>();
const CACHE_TTL = 300000; // 5 minutes cache

// Performance configuration
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 200; // 200ms

/**
 * Retry operation with exponential backoff
 */
async function retryOperation<T>(
    operation: () => Promise<T>,
    maxRetries: number = MAX_RETRIES,
    initialDelay: number = INITIAL_RETRY_DELAY
): Promise<T> {
    let retries = 0;

    while (true) {
        try {
            return await operation();
        } catch (error: any) {
            retries++;

            if (retries >= maxRetries) {
                throw error;
            }

            // Skip retry for certain errors
            if (error?.code === 'PGRST116' || error?.message?.includes('duplicate key value')) {
                throw error;
            }

            const delay = initialDelay * Math.pow(2, retries - 1);
            console.warn(`Trading wallet operation failed, retrying in ${delay}ms (attempt ${retries}/${maxRetries}):`, error.message);
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }
}

// A unique salt for this application - in production, store this securely
const APP_SALT = 'EKKO_TRADING_WALLET_SALT';

export interface TradingWallet {
    id: string;
    name: string;
    public_key: string;
    created_at: string;
    owner_wallet_address: string;
    is_active: boolean;
    last_used_at?: string;
    balance?: number;
}

// Optimized create trading wallet with retry logic
export async function createTradingWallet(
    walletAddress: string,
    walletName: string
): Promise<{ tradingWallet: TradingWallet, backupString: string }> {
    try {
        return await retryOperation(async () => {
            // Generate a new keypair
            const { keypair } = generateNewKeypair();

            // Create a backup string for the user to save
            const backupString = createKeypairBackup(keypair);

            // Generate a passphrase based on the user's wallet address
            const passphrase = generatePassphrase(walletAddress, APP_SALT);

            // Encrypt the private key
            const encryptedPrivateKey = encryptPrivateKey(keypair.secretKey, passphrase);

            // Store in Supabase using optimized admin client
            const { data, error } = await adminSupabase
                .from('trading_wallets')
                .insert([
                    {
                        name: walletName,
                        public_key: keypair.publicKey.toString(),
                        encrypted_private_key: encryptedPrivateKey,
                        owner_wallet_address: walletAddress,
                        is_active: true
                    }
                ])
                .select()
                .single();

            if (error) {
                console.error("Supabase insert error:", error);
                throw new Error(`Failed to create trading wallet: ${error.message}`);
            }

            if (!data) {
                throw new Error("No data returned after creating trading wallet");
            }

            console.log("Trading wallet created successfully:", data.public_key);

            // Clear cache for this wallet owner
            tradingWalletCache.delete(walletAddress);

            return {
                tradingWallet: data as TradingWallet,
                backupString
            };
        });
    } catch (error) {
        console.error('Error creating trading wallet:', error);
        throw error;
    }
}

// Optimized get all trading wallets for a user with caching and batch balance fetching
export async function getUserTradingWallets(
    walletAddress: string,
    options: { includeBalances?: boolean; forceRefresh?: boolean } = {}
): Promise<TradingWallet[]> {
    try {
        console.log(`🔍 Fetching trading wallets for: ${walletAddress.slice(0, 8)}...`);

        // Check cache first (unless force refresh)
        if (!options.forceRefresh) {
            const cachedData = tradingWalletCache.get(walletAddress);
            if (cachedData && Date.now() - cachedData.timestamp < CACHE_TTL) {
                console.log(`📋 Using cached trading wallets (${cachedData.wallets.length} wallets)`);

                // If balances are requested and not cached, fetch them
                if (options.includeBalances && cachedData.wallets.length > 0) {
                    const hasBalances = cachedData.wallets.some(w => w.balance !== undefined);
                    if (!hasBalances) {
                        return await fetchWalletsWithBalances(cachedData.wallets);
                    }
                }

                return cachedData.wallets;
            }
        }

        return await retryOperation(async () => {
            const { data, error } = await adminSupabase
                .from('trading_wallets')
                .select('*')
                .eq('owner_wallet_address', walletAddress)
                .eq('is_active', true) // Only fetch active wallets for performance
                .order('created_at', { ascending: false });

            if (error) {
                console.error("Supabase select error:", error);
                throw new Error(`Failed to fetch trading wallets: ${error.message}`);
            }

            const wallets = data as TradingWallet[];
            console.log(`📋 Found ${wallets.length} trading wallets for: ${walletAddress.slice(0, 8)}...`);

            // Cache the wallets
            tradingWalletCache.set(walletAddress, {
                wallets: wallets,
                timestamp: Date.now()
            });

            // Fetch balances if requested
            if (options.includeBalances && wallets.length > 0) {
                return await fetchWalletsWithBalances(wallets);
            }

            return wallets;
        });
    } catch (error) {
        console.error('❌ Error fetching user trading wallets:', error);
        throw error;
    }
}

// Optimized batch balance fetching
async function fetchWalletsWithBalances(wallets: TradingWallet[]): Promise<TradingWallet[]> {
    console.log(`💰 Fetching balances for ${wallets.length} trading wallets in parallel...`);

    const startTime = performance.now();

    // Fetch all balances in parallel with limited concurrency to avoid rate limits
    const batchSize = 5; // Process 5 wallets at a time
    const walletsWithBalances: TradingWallet[] = [];

    for (let i = 0; i < wallets.length; i += batchSize) {
        const batch = wallets.slice(i, i + batchSize);

        const batchPromises = batch.map(async (wallet) => {
            try {
                const balanceInLamports = await getBalance(wallet.public_key);
                return {
                    ...wallet,
                    balance: balanceInLamports / 1_000_000_000 // Convert lamports to SOL
                };
            } catch (error) {
                console.error(`💰 Error fetching balance for wallet ${wallet.public_key.slice(0, 8)}...:`, error);
                return wallet; // Return original wallet without balance on error
            }
        });

        const batchResults = await Promise.all(batchPromises);
        walletsWithBalances.push(...batchResults);

        // Small delay between batches to be nice to the RPC
        if (i + batchSize < wallets.length) {
            await new Promise(resolve => setTimeout(resolve, 100));
        }
    }

    const duration = performance.now() - startTime;
    console.log(`💰 Successfully fetched balances in ${duration.toFixed(2)}ms`);

    return walletsWithBalances;
}

// Optimized restore keypair with better error handling and retry logic
export async function restoreTradingWalletKeypair(
    walletAddress: string,
    tradingWalletPublicKey: string
): Promise<Keypair | null> {
    try {
        return await retryOperation(async () => {
            // Get the encrypted private key from Supabase
            const { data, error } = await adminSupabase
                .from('trading_wallets')
                .select('encrypted_private_key')
                .eq('owner_wallet_address', walletAddress)
                .eq('public_key', tradingWalletPublicKey)
                .eq('is_active', true)
                .single();

            if (error || !data) {
                console.error('Trading wallet not found or not accessible:', error);
                return null;
            }

            // Generate the same passphrase used for encryption
            const passphrase = generatePassphrase(walletAddress, APP_SALT);

            try {
                // Decrypt the private key
                const secretKey = decryptPrivateKey(data.encrypted_private_key, passphrase);

                // Validate the keypair
                const keypair = Keypair.fromSecretKey(secretKey);

                // Verify the public key matches
                if (keypair.publicKey.toString() !== tradingWalletPublicKey) {
                    throw new Error('Decrypted keypair public key does not match expected public key');
                }

                return keypair;
            } catch (decryptError) {
                console.error('Decryption failed:', decryptError);
                throw new Error(`Failed to decrypt trading wallet: ${decryptError instanceof Error ? decryptError.message : 'Unknown error'}`);
            }
        });
    } catch (error) {
        console.error('Error restoring trading wallet keypair:', error);
        return null;
    }
}

// Optimized delete with cache invalidation
export async function deleteTradingWallet(
    walletAddress: string,
    tradingWalletPublicKey: string
): Promise<boolean> {
    try {
        return await retryOperation(async () => {
            // Perform a soft delete by setting is_active to false
            const { error } = await adminSupabase
                .from('trading_wallets')
                .update({ is_active: false })
                .eq('owner_wallet_address', walletAddress)
                .eq('public_key', tradingWalletPublicKey);

            if (error) {
                console.error("Supabase update error:", error);
                throw new Error(`Failed to delete trading wallet: ${error.message}`);
            }

            // Clear cache for this wallet owner
            tradingWalletCache.delete(walletAddress);

            return true;
        });
    } catch (error) {
        console.error('Error deleting trading wallet:', error);
        throw error;
    }
}

// Optimized rename with cache invalidation
export async function renameTradingWallet(
    walletAddress: string,
    tradingWalletPublicKey: string,
    newName: string
): Promise<TradingWallet> {
    try {
        return await retryOperation(async () => {
            // Update the wallet name
            const { data, error } = await adminSupabase
                .from('trading_wallets')
                .update({ name: newName })
                .eq('owner_wallet_address', walletAddress)
                .eq('public_key', tradingWalletPublicKey)
                .eq('is_active', true)
                .select()
                .single();

            if (error) {
                console.error("Supabase update error:", error);
                throw new Error(`Failed to rename trading wallet: ${error.message}`);
            }

            if (!data) {
                throw new Error("No data returned after renaming trading wallet");
            }

            console.log("Trading wallet renamed successfully:", data.public_key);

            // Clear cache for this wallet owner
            tradingWalletCache.delete(walletAddress);

            return data as TradingWallet;
        });
    } catch (error) {
        console.error('Error renaming trading wallet:', error);
        throw error;
    }
}

// Get private key backup for a trading wallet
export async function getPrivateKeyBackup(
    walletAddress: string,
    tradingWalletPublicKey: string,
    exportPassword?: string
): Promise<string> {
    try {
        return await retryOperation(async () => {
            // Get the encrypted private key from Supabase (same as transactions)
            const { data, error } = await adminSupabase
                .from('trading_wallets')
                .select('encrypted_private_key')
                .eq('owner_wallet_address', walletAddress)
                .eq('public_key', tradingWalletPublicKey)
                .eq('is_active', true)
                .single();

            if (error || !data) {
                console.error('Trading wallet not found or not accessible:', error);
                throw new Error("Trading wallet not found");
            }

            // Generate the same passphrase used for encryption (same as transactions)
            const passphrase = generatePassphrase(walletAddress, APP_SALT);

            // Decrypt the private key (same as transactions)
            const secretKey = decryptPrivateKey(data.encrypted_private_key, passphrase);

            // Always return raw backup format (same as wallet creation)
            // Password is only for access control, not for encrypting the backup
            const backupString = uint8ArrayToString(secretKey);
            return backupString;
        });
    } catch (error) {
        console.error('Error getting private key backup:', error);
        throw error;
    }
}
